import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import ListPage from '@components/common/layout/ListPage';
import { commonStatus,DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import {
    addEdge,
    Background,
    Controls,
    MarkerType,
    MiniMap,
    ReactFlow,
    useEdgesState,
    useNodesState,
} from '@xyflow/react';
import { Button, Form, Input, Modal, Spin } from 'antd';

import GroupNodeComponent from './GroupNodeComponent';

import '@xyflow/react/dist/style.css';

const message = defineMessages({
    objectName: 'Device group',
});

const nodeTypes = { groupNode: GroupNodeComponent };

const defaultEdgeOptions = {
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#1677ff', strokeWidth: 2 },
};

// Kiểm tra xem có đường đi (theo hướng cạnh) từ fromId tới toId hay không,
// dùng để phát hiện vòng lặp và cạnh dư thừa (trùng quan hệ phụ thuộc) khi nối node.
const pathExists = (fromId, toId, edgeList) => {
    if (fromId === toId) return true;
    const adjacency = {};
    edgeList.forEach((e) => {
        if (!adjacency[e.source]) adjacency[e.source] = [];
        adjacency[e.source].push(e.target);
    });

    const visited = new Set([fromId]);
    const queue = [fromId];
    while (queue.length > 0) {
        const current = queue.shift();
        for (const next of adjacency[current] || []) {
            if (next === toId) return true;
            if (!visited.has(next)) {
                visited.add(next);
                queue.push(next);
            }
        }
    }
    return false;
};

// Với mỗi cạnh (u -> v), bỏ cạnh đó ra rồi kiểm tra phần còn lại của đồ thị:
// - Nếu vẫn còn đường đi khác từ u tới v -> cạnh này dư thừa/trùng quan hệ phụ thuộc.
// - Nếu có đường đi từ v về lại u -> cạnh này là một phần của vòng lặp (cycle).
// Cách tính lại toàn bộ theo từng cạnh giúp: khi 1 vòng lặp được tạo thành, TẤT CẢ
// các cạnh nằm trong vòng lặp đó đều được đánh dấu đỏ, không chỉ cạnh vừa nối.
const computeInvalidEdgeIds = (edgeList) => {
    const invalidIds = new Set();
    edgeList.forEach((edge) => {
        if (edge.source === edge.target) {
            invalidIds.add(edge.id);
            return;
        }
        const restEdges = edgeList.filter((e) => e.id !== edge.id);
        const isRedundant = pathExists(edge.source, edge.target, restEdges);
        const wouldCycle = pathExists(edge.target, edge.source, restEdges);
        if (isRedundant || wouldCycle) {
            invalidIds.add(edge.id);
        }
    });
    return invalidIds;
};

const GroupDevice = forwardRef(({ onDirtyChange }, ref) => {
    const translate = useTranslate();
    const notification = useNotification();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const deviceId = searchParams.get('deviceId');

    const [nodes, setNodes, onNodesChangeBase] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [, setHasChangesState] = useState(false);
    const hasChangesRef = useRef(false);

    // Tính lại toàn bộ các cạnh sai logic (vòng lặp hoặc dư thừa) mỗi khi danh sách cạnh thay đổi.
    const invalidEdgeIds = useMemo(() => computeInvalidEdgeIds(edges), [edges]);

    // Danh sách cạnh dùng để hiển thị: tô đỏ tất cả cạnh nằm trong vòng lặp/dư thừa.
    const displayEdges = useMemo(
        () =>
            edges.map((e) => {
                const invalid = invalidEdgeIds.has(e.id);
                return {
                    ...e,
                    style: invalid ? { stroke: '#ff4d4f', strokeWidth: 2 } : { stroke: '#1677ff', strokeWidth: 2 },
                    data: { ...e.data, invalid },
                };
            }),
        [edges, invalidEdgeIds],
    );

    const setHasChanges = useCallback((value) => {
        setHasChangesState(value);
        hasChangesRef.current = value;
        onDirtyChange?.(value);
    }, [onDirtyChange]);

    // Modal state for create/edit
    const [modalOpen, setModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null); // null = create, object = edit
    const [form] = Form.useForm();

    const { data, mixinFuncs, loading } = useListBase({
        apiConfig: apiConfig.printerGroup,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.getItemDetailLink = (dataRow) => {
                let currentPath = window.location.pathname;
                const sp = location.search;
                return `${currentPath}/${dataRow.id}${sp}`;
            };
        },
    });

    const onNodesChange = useCallback(
        (changes) => {
            onNodesChangeBase(changes);
            // Only mark changed when user finishes dragging (dragging goes from true to false)
            if (changes.some((c) => c.type === 'position' && c.dragging === false && c.position)) {
                setHasChanges(true);
            }
        },
        [onNodesChangeBase],
    );

    const { execute: executeUpdateWait } = useFetch(apiConfig.printerGroup.updateWait);
    const { execute: executeCreate, loading: loadingCreate } = useFetch(apiConfig.printerGroup.create);
    const { execute: executeUpdate, loading: loadingUpdate } = useFetch(apiConfig.printerGroup.update);

    // Guard: if hasChanges, show confirm before executing action
    // Uses ref to avoid stale closure in node callbacks
    const confirmIfUnsaved = useCallback(
        (action, contentMessage) => {
            if (hasChangesRef.current) {
                Modal.confirm({
                    title: translate.formatMessage(commonMessage.unsavedChangesContent),
                    content: contentMessage || '',
                    okText: translate.formatMessage(commonMessage.continue),
                    cancelText: translate.formatMessage(commonMessage.cancel),
                    centered: true,
                    onOk: () => {
                        setHasChanges(false);
                        action();
                    },
                });
            } else {
                action();
            }
        },
        [],
    );

    // Open modal for creating a new group
    const openCreateModal = useCallback(() => {
        confirmIfUnsaved(() => {
            setEditingGroup(null);
            form.resetFields();
            setModalOpen(true);
        }, translate.formatMessage(commonMessage.unsavedAddGroup));
    }, [form, confirmIfUnsaved]);

    // Open modal for editing an existing group
    const openEditModal = useCallback(
        (group) => {
            confirmIfUnsaved(() => {
                setEditingGroup(group);
                form.setFieldsValue({ name: group.name });
                setModalOpen(true);
            }, translate.formatMessage(commonMessage.unsavedEditGroup));
        },
        [form, confirmIfUnsaved],
    );

    // Handle modal submit (create or update)
    const handleModalOk = useCallback(() => {
        form.validateFields().then((values) => {
            if (editingGroup) {
                // Update existing group
                executeUpdate({
                    data: {
                        id: editingGroup.id,
                        name: values.name,
                        deviceId: Number(deviceId),
                        status: editingGroup.status,
                    },
                    onCompleted: () => {
                        notification({
                            type: 'success',
                            message: translate.formatMessage(commonMessage.updateSuccess),
                        });
                        setModalOpen(false);
                        setHasChanges(false);
                        mixinFuncs.getList();
                    },
                    onError: () => {
                        notification({ type: 'error', message: translate.formatMessage(commonMessage.updateError) });
                    },
                });
            } else {
                // Position new node to the right of the rightmost existing node
                const maxX = nodes.length > 0 ? Math.max(...nodes.map((n) => n.position.x)) : -260;
                const coordinate = JSON.stringify({
                    positionX: maxX + 320,
                    positionY: 40,
                });

                // Create new group
                executeCreate({
                    data: {
                        name: values.name,
                        deviceId: Number(deviceId),
                        status: commonStatus.ACTIVE,
                        coordinate,
                    },
                    onCompleted: () => {
                        notification({
                            type: 'success',
                            message: translate.formatMessage(commonMessage.updateSuccess),
                        });
                        setModalOpen(false);
                        setHasChanges(false);
                        mixinFuncs.getList();
                    },
                    onError: () => {
                        notification({ type: 'error', message: translate.formatMessage(commonMessage.updateError) });
                    },
                });
            }
        });
    }, [editingGroup, deviceId, form]);

    // Build nodes & edges from fetched data, auto-layout by dependency level
    useEffect(() => {
        if (!data || data.length === 0) return;

        const hasEditPerm = mixinFuncs.hasPermission([apiConfig.printerGroup.update.baseURL]);
        const hasDeletePerm = mixinFuncs.hasPermission([apiConfig.printerGroup.delete.baseURL]);

        // Build edges first to calculate dependency levels
        const edgeList = data
            .filter((g) => g.waitForNextGroup)
            .flatMap((g) =>
                g.waitForNextGroup
                    .split(',')
                    .map((srcId) => srcId.trim())
                    .filter((srcId) => /^\d+$/.test(srcId))
                    .map((srcId) => ({
                        id: `${srcId}-${g.id}`,
                        source: srcId,
                        target: String(g.id),
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        style: { stroke: '#1677ff', strokeWidth: 2 },
                    })),
            );

        // Calculate dependency depth for each node
        const incomingMap = {};
        const sourceMap = {};
        data.forEach((g) => {
            const id = String(g.id);
            incomingMap[id] = [];
            sourceMap[id] = [];
        });
        edgeList.forEach((e) => {
            if (incomingMap[e.target]) incomingMap[e.target].push(e.source);
            if (sourceMap[e.source]) sourceMap[e.source].push(e.target);
        });

        const levels = {};
        const resolved = new Set();
        const allIds = data.map((g) => String(g.id));

        let currentLevel = 0;
        let queue = allIds.filter((id) => incomingMap[id].length === 0);
        while (queue.length > 0) {
            const nextQueue = [];
            queue.forEach((id) => {
                if (!resolved.has(id)) {
                    levels[id] = currentLevel;
                    resolved.add(id);
                }
            });
            allIds.forEach((id) => {
                if (!resolved.has(id)) {
                    const allSourcesResolved = incomingMap[id].every((src) => resolved.has(src));
                    if (allSourcesResolved) nextQueue.push(id);
                }
            });
            currentLevel++;
            queue = nextQueue;
            if (currentLevel > allIds.length) break;
        }
        allIds.forEach((id) => {
            if (levels[id] === undefined) levels[id] = currentLevel;
        });

        // Parse saved coordinate or fallback to auto-layout
        const parseCoordinate = (coordinateStr) => {
            if (!coordinateStr) return null;
            try {
                const parsed = JSON.parse(coordinateStr);
                if (parsed.positionX != null && parsed.positionY != null) {
                    return { x: parsed.positionX, y: parsed.positionY };
                }
            } catch {
                // ignore invalid JSON
            }
            return null;
        };

        const COL_WIDTH = 320;
        const ROW_HEIGHT = 160;

        // Separate nodes with/without saved coordinates
        const savedPositions = {};
        const unsavedIds = [];
        data.forEach((g) => {
            const pos = parseCoordinate(g.coordinate);
            if (pos) {
                savedPositions[String(g.id)] = pos;
            } else {
                unsavedIds.push(String(g.id));
            }
        });

        // For unsaved nodes: if other nodes have positions, place to the right of the rightmost
        // Otherwise fallback to auto-layout by dependency level
        const positionMap = {};
        if (unsavedIds.length > 0) {
            const allPositions = Object.values(savedPositions);
            if (allPositions.length > 0) {
                // Place unsaved nodes to the right of the rightmost existing node
                const maxX = Math.max(...allPositions.map((p) => p.x));
                unsavedIds.forEach((id, idx) => {
                    positionMap[id] = {
                        x: maxX + COL_WIDTH,
                        y: 40 + idx * ROW_HEIGHT,
                    };
                });
            } else {
                // No saved positions at all — full auto-layout
                const levelBuckets = {};
                allIds.forEach((id) => {
                    const lvl = levels[id];
                    if (!levelBuckets[lvl]) levelBuckets[lvl] = [];
                    levelBuckets[lvl].push(id);
                });

                Object.entries(levelBuckets).forEach(([lvl, ids]) => {
                    ids.forEach((id, rowIdx) => {
                        positionMap[id] = {
                            x: 60 + Number(lvl) * COL_WIDTH,
                            y: 40 + rowIdx * ROW_HEIGHT,
                        };
                    });
                });
            }
        }

        const newNodes = data.map((g) => ({
            id: String(g.id),
            type: 'groupNode',
            position: savedPositions[String(g.id)] || positionMap[String(g.id)] || { x: 60, y: 40 },
            data: {
                label: g.name,
                status: g.status,
                serverName: g.server?.name,
                onEdit: hasEditPerm ? () => openEditModal(g) : null,
                onDelete: hasDeletePerm ? () => confirmIfUnsaved(() => mixinFuncs.showDeleteItemConfirm(g.id), translate.formatMessage(commonMessage.unsavedDeleteGroup)) : null,
            },
        }));

        setNodes(newNodes);
        setEdges(edgeList);
        setHasChanges(false);
    }, [data]);

    const onConnect = useCallback(
        (params) => {
            const exists = edges.some((e) => e.source === params.source && e.target === params.target);
            if (exists) return;
            if (params.source === params.target) return;

            // Màu đỏ/hợp lệ của cạnh được tính lại tự động cho toàn bộ đồ thị (xem displayEdges),
            // nên ở đây chỉ cần thêm cạnh mới với style mặc định.
            setEdges((eds) =>
                addEdge(
                    {
                        ...params,
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        style: { stroke: '#1677ff', strokeWidth: 2 },
                    },
                    eds,
                ),
            );
            setHasChanges(true);
        },
        [edges, setEdges],
    );

    const onEdgeClick = useCallback(
        (_event, edge) => {
            setEdges((eds) => eds.filter((e) => e.id !== edge.id));
            setHasChanges(true);
        },
        [setEdges],
    );

    const handleSave = useCallback(() => {
        // Chặn lưu nếu còn cạnh nối sai logic (vòng lặp hoặc dư thừa)
        if (invalidEdgeIds.size > 0) {
            notification({ type: 'error', message: translate.formatMessage(commonMessage.invalidConnection) });
            return;
        }

        // Tìm các nhóm mà mỗi node đang chờ (dependencies)
        const waitMap = {}; // { nodeId: [các node mà nó chờ] }
        nodes.forEach((n) => { waitMap[n.id] = []; });
        edges.forEach((e) => {
            if (waitMap[e.target]) waitMap[e.target].push(e.source);
        });

        // Tính sortId = đợt ra món
        // - Node không chờ ai → sortId = 0 (ra đầu tiên)
        // - Node chờ node khác → sortId = max(sortId của các node nó chờ) + 1
        const sortIdMap = {};

        const computeSortId = (id, visiting = new Set()) => {
            if (sortIdMap[id] !== undefined) return sortIdMap[id];

            // Phát hiện vòng lặp: nếu đang tính node này mà gặp lại nó → cycle
            if (visiting.has(id)) return -1;
            visiting.add(id);

            const deps = waitMap[id] || [];
            if (deps.length === 0) {
                sortIdMap[id] = 0;
                return 0;
            }

            let maxDepLevel = 0;
            for (const depId of deps) {
                const depLevel = computeSortId(depId, visiting);
                if (depLevel === -1) return -1; // cycle
                maxDepLevel = Math.max(maxDepLevel, depLevel);
            }

            sortIdMap[id] = maxDepLevel + 1;
            return sortIdMap[id];
        };

        // Tính sortId cho tất cả nodes
        const hasCycle = nodes.some((n) => computeSortId(n.id) === -1);
        if (hasCycle) {
            notification({ type: 'error', message: translate.formatMessage(commonMessage.cyclicDependency) });
            return;
        }

        const printerGroups = nodes.map((node) => {
            const incomingSources = waitMap[node.id];

            const group = {
                id: Number(node.id),
                sortId: sortIdMap[node.id],
                coordinate: JSON.stringify({
                    positionX: node.position.x,
                    positionY: node.position.y,
                }),
            };

            if (incomingSources.length > 0) {
                group.waitForNextGroup = incomingSources.join(',');
            }

            return group;
        });

        executeUpdateWait({
            data: {
                deviceId: Number(deviceId),
                printerGroups,
            },
            onCompleted: () => {
                notification({ type: 'success', message: translate.formatMessage(commonMessage.updateSuccess) });
                setHasChanges(false);
                mixinFuncs.getList();
            },
            onError: () => {
                notification({ type: 'error', message: translate.formatMessage(commonMessage.updateError) });
            },
        });
    }, [edges, nodes, deviceId, invalidEdgeIds]);

    useImperativeHandle(ref, () => ({
        openCreateModal,
        handleSave,
    }), [openCreateModal, handleSave]);

    const renderActionBar = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', gap: 12 }}>
                <BaseTooltip title={translate.formatMessage(commonMessage.create)}>
                    <Button
                        type="primary"
                        onClick={() => openCreateModal()}
                        icon={<PlusOutlined />}
                    >
                    </Button>
                </BaseTooltip>
                <BaseTooltip title={translate.formatMessage(commonMessage.save)}>
                    <Button
                        type="primary"
                        disabled={nodes.length === 0 || !hasChangesRef.current}
                        onClick={() => handleSave()}
                        icon={<SaveOutlined />}
                    >
                    </Button>
                </BaseTooltip>
                
            </div >
        );
    };

    return (
        <ListPage
            actionBar={renderActionBar()}
            baseTable={
                <div style={{ display: 'flex', flexDirection: 'column', height: '70vh' }}>
                    <Spin spinning={loading} wrapperClassName="group-device-spin">
                        <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, height: '70vh' }}>
                            <ReactFlow
                                nodes={nodes}
                                edges={displayEdges}
                                onNodesChange={onNodesChange}
                                onEdgesChange={onEdgesChange}
                                onConnect={onConnect}
                                onEdgeClick={onEdgeClick}
                                nodeTypes={nodeTypes}
                                defaultEdgeOptions={defaultEdgeOptions}
                                fitView
                                fitViewOptions={{ padding: 0.3 }}
                                // proOptions={{ hideAttribution: true }}
                            >
                                <Background />
                                {/* <Controls /> */}
                                {/* <MiniMap /> */}
                            </ReactFlow>
                        </div>
                    </Spin>

                    <Modal
                        title={
                            editingGroup ? (
                                <FormattedMessage defaultMessage="Edit Group" />
                            ) : (
                                <FormattedMessage defaultMessage="Add Group" />
                            )
                        }
                        open={modalOpen}
                        onOk={handleModalOk}
                        onCancel={() => setModalOpen(false)}
                        confirmLoading={loadingCreate || loadingUpdate}
                        destroyOnClose
                    >
                        <Form form={form} layout="vertical" preserve={false}>
                            <Form.Item
                                name="name"
                                label={<FormattedMessage defaultMessage="Name" />}
                                rules={[{ required: true, message: translate.formatMessage(commonMessage.requireGroupName) }]}
                            >
                                <Input placeholder={translate.formatMessage(commonMessage.placeholderGroupName)} />
                            </Form.Item>
                        </Form>
                    </Modal>
                </div>
            }
        />
    );
});

GroupDevice.displayName = 'GroupDevice';

export default GroupDevice;
